import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../services/api';
import { PenkiBadge } from './PenkiBadge';
import { formatPenki } from '../utils/penki';
import type { ShopProduct, ShopPurchase } from '../types';
import './HomePage.css';
import './MyDropsPage.css';
import './ShopPage.css';

type ShopTab = 'catalog' | 'purchased';

export const ShopPage: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab: ShopTab = params.get('tab') === 'purchased' ? 'purchased' : 'catalog';

  const [products, setProducts] = useState<ShopProduct[]>([]);
  const [purchases, setPurchases] = useState<ShopPurchase[]>([]);
  const [purchaseTotal, setPurchaseTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const [catalog, mine] = await Promise.all([
          apiClient.getShopProducts(),
          apiClient.getShopPurchases(),
        ]);
        if (!cancelled) {
          setProducts(catalog);
          setPurchases(mine.data);
          setPurchaseTotal(mine.pagination.total);
        }
      } catch (error) {
        console.error('Failed to load shop', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="page-container shop-page">
      <PenkiBadge />

      <div className="drops-tabs-sticky">
        <div className="drops-tabs">
          <button
            type="button"
            className={`drops-tab ${tab === 'catalog' ? 'active' : ''}`}
            onClick={() => setParams({})}
          >
            Каталог
          </button>
          <button
            type="button"
            className={`drops-tab ${tab === 'purchased' ? 'active' : ''}`}
            onClick={() => setParams({ tab: 'purchased' })}
          >
            Мои покупки <span className="tab-count">{purchaseTotal}</span>
          </button>
        </div>
      </div>

      <div className="shop-body">
        {loading ? (
          <div className="catalog-loading">
            <div className="catalog-spinner" />
          </div>
        ) : tab === 'catalog' ? (
          products.length === 0 ? (
            <div className="catalog-empty">
              <p>Товары скоро появятся</p>
            </div>
          ) : (
            <div className="catalog-grid">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="catalog-nft-card shop-product-card"
                  onClick={() => navigate(`/shop/${product.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      navigate(`/shop/${product.id}`);
                    }
                  }}
                >
                  <div className="catalog-nft-image-container">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.title} className="catalog-nft-image" />
                    ) : (
                      <div className="catalog-nft-placeholder shop-card-art" />
                    )}
                  </div>
                  <div className="catalog-nft-info">
                    <span className="catalog-nft-price">{formatPenki(product.price_penki)} УЙМ</span>
                    <h3 className="catalog-nft-title">{product.title}</h3>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : purchases.length === 0 ? (
          <div className="catalog-empty">
            <p>Покупок пока нет</p>
          </div>
        ) : (
          <div className="catalog-grid">
            {purchases.map((purchase) => (
              <div
                key={purchase.id}
                className="catalog-nft-card shop-product-card"
                onClick={() => navigate(`/purchases/${purchase.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(`/purchases/${purchase.id}`);
                  }
                }}
              >
                <div className="catalog-nft-image-container">
                  {purchase.product?.image_url ? (
                    <img
                      src={purchase.product.image_url}
                      alt={purchase.product.title}
                      className="catalog-nft-image"
                    />
                  ) : (
                    <div className="catalog-nft-placeholder shop-card-art" />
                  )}
                </div>
                <div className="catalog-nft-info">
                  <h3 className="catalog-nft-title">{purchase.product?.title ?? 'Товар'}</h3>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
